/**
 * RecoveryCalendar — Today / Tomorrow / This Week / Overdue buckets
 * with quick actions: Call, WhatsApp (template picker), Add Recovery Note.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, fmtPKR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { WA_TEMPLATES, renderTemplate, waLink } from "../lib/waTemplates";
import {
  Phone, MessageCircle, StickyNote, Calendar, Clock, AlertTriangle, Users,
} from "lucide-react";
import PageTip from "../components/PageTip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

function Bucket({ icon: Icon, title, items, tone, openWA, openNote, shop }) {
  const cls = {
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50",
    red: "border-red-200 bg-red-50",
  }[tone] || "border-slate-200 bg-slate-50";
  return (
    <div className={`border ${cls} rounded-md`} data-testid={`bucket-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="px-4 py-3 border-b border-black/10 flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-700" />
        <div className="font-display font-semibold">{title}</div>
        <span className="text-xs text-slate-500">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">Koi customer nahi.</div>
      ) : (
        <div className="divide-y divide-black/5">
          {items.map((r) => (
            <div key={r.loan_id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`rc-row-${r.loan_id}`}>
              <div className="flex-1 min-w-[180px]">
                <a href={`/customers/${r.customer_id}`} className="font-semibold hover:underline">
                  {r.customer_name || "—"}
                </a>
                <div className="text-xs text-slate-500">
                  {r.product_name} · {r.salesman_name ? `Salesman: ${r.salesman_name}` : "Unassigned"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold tabular">{fmtPKR(r.amount)}</div>
                <div className="text-xs text-slate-500">
                  Due {r.due_date}{r.days_late > 0 ? ` · ${r.days_late}d late` : ""}
                </div>
              </div>
              {r.customer_mobile && (
                <a
                  href={`tel:${r.customer_mobile}`}
                  title="Call"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-slate-200 hover:bg-sky-50 hover:border-sky-400 text-sky-700"
                  data-testid={`rc-call-${r.loan_id}`}
                ><Phone className="w-4 h-4" /></a>
              )}
              <button
                onClick={() => openWA(r)}
                title="WhatsApp"
                className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-slate-200 hover:bg-emerald-50 hover:border-emerald-500 text-emerald-700"
                data-testid={`rc-wa-${r.loan_id}`}
              ><MessageCircle className="w-4 h-4" /></button>
              <button
                onClick={() => openNote(r)}
                title="Add recovery note"
                className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-slate-200 hover:bg-amber-50 hover:border-amber-500 text-amber-700"
                data-testid={`rc-note-${r.loan_id}`}
              ><StickyNote className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RecoveryCalendar() {
  const { user } = useAuth();
  const [data, setData] = useState({ today: [], tomorrow: [], this_week: [], overdue: [] });
  const [waOpen, setWaOpen] = useState(false);
  const [waRow, setWaRow] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteRow, setNoteRow] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get("/recovery/calendar").then((r) => setData(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openWA = (row) => { setWaRow(row); setWaOpen(true); };
  const openNote = (row) => { setNoteRow(row); setNote(""); setNoteOpen(true); };

  const sendTemplate = (tpl) => {
    if (!waRow?.customer_mobile) { toast.error("Customer mobile missing"); return; }
    const msg = renderTemplate(tpl.body, {
      name: waRow.customer_name,
      amount: waRow.amount,
      date: waRow.due_date,
      product: waRow.product_name,
      shop: user?.shop_name || "HisaabPro",
      days_late: waRow.days_late,
    });
    const link = waLink(waRow.customer_mobile, msg);
    if (link) window.open(link, "_blank");
    setWaOpen(false);
  };

  const saveNote = async () => {
    if (!note.trim()) { toast.error("Note khali nahi ho sakta"); return; }
    try {
      await api.put(`/customers/${noteRow.customer_id}/admin-control`, {
        recovery_notes: note,
      });
      toast.success("Note saved");
      setNoteOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const total = data.today.length + data.tomorrow.length + data.this_week.length + data.overdue.length;

  return (
    <div className="space-y-6" data-testid="recovery-calendar">
      <PageTip
        id="recovery-calendar"
        icon={Calendar}
        text="View today's due customers and overdue accounts for better recovery management."
      />
      <div>
        <h1 className="text-2xl font-display font-bold">Recovery Calendar</h1>
        <div className="text-sm text-slate-500">
          {total} customer(s) need recovery action — {data.overdue.length} overdue
        </div>
      </div>

      <Bucket icon={AlertTriangle} title="Overdue" tone="red" items={data.overdue} openWA={openWA} openNote={openNote} shop={user?.shop_name} />
      <Bucket icon={Calendar} title="Today's Due" tone="emerald" items={data.today} openWA={openWA} openNote={openNote} shop={user?.shop_name} />
      <Bucket icon={Clock} title="Tomorrow" tone="sky" items={data.tomorrow} openWA={openWA} openNote={openNote} shop={user?.shop_name} />
      <Bucket icon={Users} title="This Week" tone="amber" items={data.this_week} openWA={openWA} openNote={openNote} shop={user?.shop_name} />

      {/* WhatsApp template picker */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-md" data-testid="wa-template-dialog">
          <DialogHeader>
            <DialogTitle>WhatsApp template · {waRow?.customer_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {WA_TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => sendTemplate(t)}
                className="w-full text-left p-3 border border-slate-200 rounded-md hover:border-emerald-500 hover:bg-emerald-50"
                data-testid={`wa-tpl-${t.key}`}
              >
                <div className="font-semibold flex items-center gap-2">
                  <span>{t.icon}</span> {t.label}
                </div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap">
                  {t.body.slice(0, 100)}…
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="max-w-md" data-testid="rc-note-dialog">
          <DialogHeader>
            <DialogTitle>Recovery note · {noteRow?.customer_name}</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., Customer ne kal payment ka promise kiya hai…" data-testid="rc-note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={saveNote} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rc-note-save">Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
